import { useEffect, useRef, useState, useCallback } from 'react';
import type { Workflow } from '@n8n-builder/n8n-types';
import '@n8n_io/n8n-demo-component';

// Use proxy URL to intercept save requests and prevent 404 errors
// Defaults to deployed Cloudflare Worker; override via VITE_PREVIEW_PROXY_URL env var
const DEFAULT_PREVIEW_PROXY_URL = 'https://n8n-preview-proxy.nlschnell0413.workers.dev/workflows/demo';
const PREVIEW_PROXY_URL = import.meta.env.VITE_PREVIEW_PROXY_URL || DEFAULT_PREVIEW_PROXY_URL;

const MAX_RETRIES = 2;

interface WorkflowCanvasProps {
  workflow: Workflow;
  tidyUp?: boolean;
}

export function WorkflowCanvas({ workflow, tidyUp = true }: WorkflowCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const demoRef = useRef<HTMLElement | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const retryKeyRef = useRef(0);
  const dimensionsRef = useRef<{ width: number; height: number } | null>(null);

  useEffect(() => {
    dimensionsRef.current = dimensions;
  }, [dimensions]);

  useEffect(() => {
    retryKeyRef.current = retryKey;
  }, [retryKey]);

  const updateDimensions = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.height > 50 && rect.width > 50) {
      setDimensions({ width: rect.width, height: rect.height });
    }
  }, []);

  // Dimension detection + ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    updateDimensions();
    const t1 = setTimeout(updateDimensions, 50);
    const t2 = setTimeout(updateDimensions, 150);
    const raf = requestAnimationFrame(updateDimensions);

    const resizeObserver = new ResizeObserver(() => updateDimensions());
    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
    };
  }, [updateDimensions]);

  const hasDimensions = dimensions !== null;

  // Create the n8n-demo element ONCE, only recreate on retry (error recovery)
  useEffect(() => {
    if (!containerRef.current || !hasDimensions) return;

    // Remove old demo element if it exists
    if (demoRef.current && containerRef.current.contains(demoRef.current)) {
      containerRef.current.removeChild(demoRef.current);
      demoRef.current = null;
    }

    const demoElement = document.createElement('n8n-demo');
    demoElement.setAttribute('workflow', JSON.stringify(workflow));

    if (PREVIEW_PROXY_URL) {
      demoElement.setAttribute('src', PREVIEW_PROXY_URL);
    }
    if (tidyUp) {
      demoElement.setAttribute('tidyup', 'true');
    }

    demoElement.style.display = 'block';
    const dims = dimensionsRef.current;
    if (dims) {
      demoElement.style.width = `${dims.width}px`;
      demoElement.style.height = `${dims.height}px`;
      demoElement.style.setProperty('--n8n-workflow-min-height', `${dims.height}px`);
    }

    containerRef.current.appendChild(demoElement);
    demoRef.current = demoElement;

    // Monitor for error state and retry
    let errorCheckTimer: ReturnType<typeof setTimeout> | undefined;
    if (retryKey < MAX_RETRIES) {
      errorCheckTimer = setTimeout(() => {
        if (demoRef.current && (demoRef.current as any).error === true) {
          setRetryKey((k) => k + 1);
        }
      }, 3000);
    }

    return () => {
      clearTimeout(errorCheckTimer);
      if (demoRef.current && containerRef.current?.contains(demoRef.current)) {
        containerRef.current.removeChild(demoRef.current);
        demoRef.current = null;
      }
    };
    // Note: workflow intentionally NOT in deps — updates handled by the effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tidyUp, hasDimensions, retryKey]);

  // When workflow changes, update the existing n8n-demo element instead of recreating it.
  // Posts the workflow directly to the iframe via postMessage, bypassing the n8n-demo
  // component's n8nReady/iframeVisible flags which may not be set after init errors.
  useEffect(() => {
    const demo = demoRef.current;
    if (!demo) return;

    const workflowJson = JSON.stringify(workflow);
    demo.setAttribute('workflow', workflowJson);

    // Try the component's built-in method first
    if ((demo as any).n8nReady && (demo as any).iframeVisible) {
      (demo as any).loadWorkflow();
    } else {
      // Fallback: post directly to the iframe inside the shadow DOM
      const iframe = demo.shadowRoot?.getElementById('int_iframe') as HTMLIFrameElement | null;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            command: 'openWorkflow',
            workflow,
            hideNodeIssues: false,
          }),
          '*'
        );
      }
    }

    // Reset retry counter for the new workflow
    if (retryKeyRef.current !== 0) {
      setRetryKey(0);
    }
  }, [workflow]);

  // Update dimensions on existing demo element when size changes
  useEffect(() => {
    if (!demoRef.current || !dimensions) return;
    demoRef.current.style.width = `${dimensions.width}px`;
    demoRef.current.style.height = `${dimensions.height}px`;
    demoRef.current.style.setProperty('--n8n-workflow-min-height', `${dimensions.height}px`);
  }, [dimensions]);

  return (
    <div
      ref={containerRef}
      className="flex-1 h-full w-full bg-gray-50 overflow-hidden"
    />
  );
}
